import { useEffect, useState } from 'react';
import { toISODateString } from '../../../../utils/helpers/dateUtils';
import {
  AutoAssessment,
  AutoEvaluation,
  JobDescription,
  JobPostingTabData,
  JobRequisition,
} from '../../../../utils/types/JobOpenings';

export const useJobOpeningForm = (initialData: any, jobId: string) => {
  const [jobRequisition, setJobRequisition] = useState<JobRequisition>({
    id: '',
    jobRequisitionType: 'Replacement',
    applicantScope: ['External'],
    jobOpeningId: jobId,
    hiringManager: [],
    deadline: '',
    internalRecruiter: [],
    headCount: 1,
    tags: [],
  });

  const [jobDescription, setJobDescription] = useState<JobDescription>({
    id: '',
    jobOpeningId: jobId,
    jobReferenceNumber: '',
    jobReferenceNumberJD: '',
    minimumWorkExperience: 0,
    jobLocation: '',
    responsibilities: [],
    educations: [],
    certifications: [],
    mandatorySkills: [],
    desirableSkills: [],
    behavioralAttributes: [],
    transcript: '',
    completed: false,
    jobReferenceType: 'Manual',
    jobReferenceNumberAuto: '',
    workType: 'Permanent',
  });

  const [autoSuitability, setAutoSuitability] = useState<AutoEvaluation>({
    id: '',
    jobOpeningId: jobId,
    jobLocation: '',
    age: { value: 18, operator: 'Greater Than' },
    workExperience: { value: 0, operator: 'Greater Than' },
    educations: [],
    certifications: [],
    skills: [],
    school: [],
    keywords: [],
    desirableSkills: [],
    behavioralAttributes: [],
  });

  const [autoOutstanding, setAutoOutstanding] = useState<AutoEvaluation>({
    id: '',
    jobOpeningId: jobId,
    jobLocation: '',
    age: { value: 18, operator: 'Greater Than' },
    workExperience: { value: 0, operator: 'Greater Than' },
    educations: [],
    certifications: [],
    skills: [],
    school: [],
    keywords: [],
    desirableSkills: [],
    behavioralAttributes: [],
  });

  const [autoAssessment, setAutoAssessment] = useState<AutoAssessment>({
    id: '',
    jobOpeningId: jobId,
    assessment: {
      id: '',
      assessmentType: 'candidate',
      assessmentTitle: '',
      startDate: Math.floor(Date.now() / 1000),
      endDate: Math.floor(Date.now() / 1000) + 7 * 24 * 60 * 60,
      isActive: false,
      description: '',
      organizationId: '',
      userId: '',
      creationDateTime: 0,
    },
  });

  const [jobPostingData, setJobPostingData] = useState<JobPostingTabData>({
    id: '',
    jobOpeningId: jobId,
    organizationId: '',
    jobTitle: '',
    department: '',
    location: '',
    htmlTemplate: '',
    jsonTemplate: '',
    workType: 'Office',
    active: false,
    startDate: toISODateString(new Date()),
    endDate: toISODateString(
      new Date(new Date().setDate(new Date().getDate() + 7)),
    ),
  });

  const [jobPostExist, setJobPostExist] = useState<boolean>(false);

  // Sync state from RTK Query data
  useEffect(() => {
    if (initialData) {
      if (initialData.jobRequisition)
        setJobRequisition(initialData.jobRequisition);
      if (initialData.jobDescription)
        setJobDescription(initialData.jobDescription);
      if (initialData.autoSuitability)
        setAutoSuitability(initialData.autoSuitability);
      if (initialData.autoOutstanding)
        setAutoOutstanding(initialData.autoOutstanding);
      if (initialData.autoAssessment)
        setAutoAssessment(initialData.autoAssessment);
      if (initialData.jobPosting) {
        setJobPostingData(initialData.jobPosting);
        setJobPostExist(true);
      } else {
        setJobPostExist(false);
      }
    }
  }, [initialData, jobId]);

  // Ensure jobOpeningId is always set correctly when jobId changes
  useEffect(() => {
    if (jobId && jobId.trim() !== '') {
      setJobRequisition((prev) => ({
        ...prev,
        jobOpeningId: jobId,
      }));
      setJobDescription((prev) => ({
        ...prev,
        jobOpeningId: jobId,
      }));
      setAutoSuitability((prev) => ({
        ...prev,
        jobOpeningId: jobId,
      }));
      setAutoOutstanding((prev) => ({
        ...prev,
        jobOpeningId: jobId,
      }));
      setAutoAssessment((prev) => ({
        ...prev,
        jobOpeningId: jobId,
      }));
      setJobPostingData((prev) => ({
        ...prev,
        jobOpeningId: jobId,
      }));
    }
  }, [jobId]);

  return {
    jobRequisition,
    setJobRequisition,
    jobDescription,
    setJobDescription,
    autoSuitability,
    setAutoSuitability,
    autoOutstanding,
    setAutoOutstanding,
    autoAssessment,
    setAutoAssessment,
    jobPostingData,
    setJobPostingData,
    jobPostExist,
  };
};
